import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, 
  Home, 
  Car, 
  Heart,
  Building,
  Users,
  CheckCircle,
  ArrowRight,
  FileText,
  DollarSign,
  Award,
  Clock,
  UserCheck,
  Mail
} from 'lucide-react';

const Insurance = () => {
  const [selectedCategory, setSelectedCategory] = useState('auto');

  const insuranceTypes = [
    {
      id: 'auto',
      title: 'Auto Insurance',
      description: 'Comprehensive coverage for your vehicle with competitive rates and excellent service.',
      startingRate: 'Starting at $49/month',
      features: [
        'Liability coverage',
        'Collision protection',
        'Comprehensive coverage',
        '24/7 roadside assistance',
        'Rental car coverage',
        'Glass repair services'
      ],
      color: 'from-blue-500 to-blue-600',
      icon: Car
    },
    {
      id: 'home',
      title: 'Homeowners Insurance',
      description: 'Protect your most valuable investment with our comprehensive home insurance.',
      startingRate: 'Starting at $89/month',
      features: [
        'Dwelling protection',
        'Personal property coverage',
        'Liability protection',
        'Additional living expenses',
        'Medical payments',
        'Natural disaster coverage'
      ],
      color: 'from-green-500 to-green-600',
      icon: Home
    },
    {
      id: 'life',
      title: 'Life Insurance',
      description: 'Secure your family\'s financial future with our flexible life insurance options.',
      startingRate: 'Starting at $25/month',
      features: [
        'Term life insurance',
        'Whole life insurance',
        'Flexible premium options',
        'Cash value accumulation',
        'Living benefits',
        'Family protection'
      ],
      color: 'from-purple-500 to-purple-600',
      icon: Heart
    },
    {
      id: 'business',
      title: 'Business Insurance',
      description: 'Comprehensive protection for your business operations and assets.',
      startingRate: 'Starting at $199/month',
      features: [
        'General liability',
        'Property insurance',
        'Workers compensation',
        'Professional liability',
        'Cyber liability',
        'Business interruption'
      ],
      color: 'from-orange-500 to-orange-600',
      icon: Building
    }
  ];

  const benefits = [
    {
      icon: Award,
      title: 'Trusted Partners',
      description: 'We partner with top-rated insurance companies to offer you the best coverage options.'
    },
    {
      icon: DollarSign,
      title: 'Competitive Rates',
      description: 'Get the coverage you need at prices that fit your budget with our competitive rates.'
    },
    {
      icon: UserCheck,
      title: 'Personal Service',
      description: 'Work with dedicated insurance specialists who understand your unique needs.'
    },
    {
      icon: Clock,
      title: 'Quick Claims',
      description: 'Fast and efficient claims processing to get you back on track quickly.'
    }
  ];

  const selectedInsurance = insuranceTypes.find(type => type.id === selectedCategory);

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-20 gradient-bg overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center text-white">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-5xl lg:text-6xl font-bold leading-tight mb-6"
            >
              Insurance Services
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-accent-400 to-accent-500">
                Protection & Peace of Mind
              </span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-blue-100 leading-relaxed mb-8 max-w-3xl mx-auto"
            >
              Protect what matters most with comprehensive insurance solutions from 
                              Aurora Wealth Reserves. We offer competitive rates and personalized service.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 justify-center"
            >
              <Link to="#quote" className="btn-secondary text-center">
                Get Free Quote
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/contact" className="flex items-center justify-center space-x-2 bg-white/10 backdrop-blur-sm text-white font-semibold py-3 px-6 rounded-lg border border-white/20 hover:bg-white/20 transition-all duration-200">
                <Mail className="w-4 h-4" />
                <span>Contact an Agent</span>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Insurance Types Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-secondary-900 mb-6">
              Comprehensive Coverage Options
            </h2>
            <p className="text-xl text-secondary-600 max-w-3xl mx-auto leading-relaxed">
              From auto and home to life and business insurance, we provide 
              comprehensive protection for all aspects of your life.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {insuranceTypes.map((insurance, index) => (
              <motion.div
                key={insurance.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group"
              >
                <div className="card p-8 h-full">
                  <div className="flex items-start justify-between mb-6">
                    <div className={`w-16 h-16 bg-gradient-to-r ${insurance.color} rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                      <insurance.icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-green-600">{insurance.startingRate}</div>
                    </div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-secondary-900 mb-4">{insurance.title}</h3>
                  <p className="text-secondary-600 mb-6 leading-relaxed">{insurance.description}</p>
                  
                  <ul className="space-y-3 mb-8">
                    {insurance.features.map((feature) => (
                      <li key={feature} className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <span className="text-secondary-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <div className="flex space-x-4">
                    <Link
                      to="#quote"
                      className="flex-1 btn-primary text-center"
                    >
                      Get Quote
                    </Link>
                    <Link
                      to="/contact"
                      className="flex-1 btn-secondary text-center"
                    >
                      Learn More
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-secondary-900 mb-6">
              Why Choose Our Insurance Services?
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <benefit.icon className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-bold text-secondary-900 mb-4">{benefit.title}</h3>
                <p className="text-secondary-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Form Section */}
      <section id="quote" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-secondary-900 mb-6">
              Get Your Free Insurance Quote
            </h2>
            <p className="text-xl text-secondary-600 leading-relaxed">
              Tell us a bit about yourself and we'll provide you with a personalized insurance quote.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="card p-8"
          >
            <form className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="John"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Smith"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Insurance Type
                </label>
                <select className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                  <option value="">Select Insurance Type</option>
                  <option value="auto">Auto Insurance</option>
                  <option value="home">Homeowners Insurance</option>
                  <option value="life">Life Insurance</option>
                  <option value="business">Business Insurance</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Additional Information
                </label>
                <textarea
                  rows="4"
                  className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Tell us about your specific insurance needs..."
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full btn-primary flex items-center justify-center space-x-2"
              >
                <FileText className="w-5 h-5" />
                <span>Get Free Quote</span>
              </button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 gradient-bg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to Protect What Matters Most?
            </h2>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
              Contact our insurance specialists today and discover how we can 
              help protect your family, home, and business.
            </p>
            
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 justify-center">
              <Link to="#quote" className="btn-secondary text-center">
                Get Free Quote
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <a href="mailto:insurance@aurorawealthreserves.com" className="flex items-center justify-center space-x-2 bg-white/10 backdrop-blur-sm text-white font-semibold py-3 px-6 rounded-lg border border-white/20 hover:bg-white/20 transition-all duration-200">
                <Mail className="w-4 h-4" />
                <span>Email Us</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Insurance; 